import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ServiceDetailPanel from "./ServiceDetailPanel";

type Detail = { title: string; body: string };
type Service = { number: string; title: string; calligraphy: string; intro: string; details: Detail[]; ctaExternalUrl?: string; ctaExternalLabel?: string };

export default function ServicesPreview() {
  const { t } = useTranslation();
  const services = t("servicesPreview.services", { returnObjects: true }) as Service[];
  const [activeGlobalIndex, setActiveGlobalIndex] = useState<number | null>(null);

  const getGlobalIndex = (serviceIndex: number, detailIndex: number): number => {
    if (!Array.isArray(services)) return 0;
    let count = 0;
    for (let i = 0; i < serviceIndex; i++) {
      count += Array.isArray(services[i]?.details) ? services[i].details.length : 0;
    }
    return count + detailIndex;
  };

  return (
    <>
      <section className="py-14 bg-[#E4E5EC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20">
            <span
              className="text-[#8A7A6A] block leading-none mb-2"
              style={{ fontFamily: "'Great Vibes', cursive", fontSize: "38px" }}
            >
              {t("servicesPreview.calligraphy")}
            </span>
            <h2
              className="text-[#1E202A] leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(30px, 4vw, 46px)", fontWeight: 300 }}
            >
              {t("servicesPreview.title1")}
              <br />
              <em>{t("servicesPreview.title2")}</em>
            </h2>
          </div>

          <div className="flex flex-col gap-0">
            {Array.isArray(services) && services.map((s, si) => (
              <div key={si}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 py-16">
                  <div className="lg:col-span-4">
                    <span
                      className="text-[#8A7A6A]/15 leading-none block mb-2 select-none"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "100px", fontStyle: "italic", fontWeight: 300 }}
                    >
                      {s.number}
                    </span>
                    <span
                      className="text-[#8A7A6A]/50 block leading-none mb-4"
                      style={{ fontFamily: "'Great Vibes', cursive", fontSize: "26px" }}
                    >
                      {s.calligraphy}
                    </span>
                    <h3
                      className="text-[#1E202A]"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(26px, 3vw, 36px)", fontWeight: 400 }}
                    >
                      {s.title}
                    </h3>
                  </div>

                  <div className="lg:col-span-8">
                    <p className="text-[#74758A] text-sm leading-[2] mb-10">{s.intro}</p>

                    <div className="flex flex-col gap-0 mb-10">
                      {Array.isArray(s.details) && s.details.map((d, di) => (
                        <button
                          key={di}
                          onClick={() => setActiveGlobalIndex(getGlobalIndex(si, di))}
                          className="flex items-center gap-3 py-3 text-left group border-b border-[#D0D1DB]/50 last:border-b-0 hover:border-[#8A7A6A]/40 transition-all duration-200 cursor-pointer"
                        >
                          <div className="mt-0.5 w-1 h-1 rounded-full bg-[#8A7A6A] shrink-0 group-hover:scale-150 transition-transform duration-200" />
                          <span className="text-sm text-[#1E202A]/75 leading-relaxed group-hover:text-[#8A7A6A] transition-colors duration-200 flex-1">
                            {d.title}
                          </span>
                          <span className="text-xs font-medium text-[#8A7A6A] bg-[#8A7A6A]/10 px-2.5 py-1 rounded-full opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-200 whitespace-nowrap shrink-0">
                            {t("servicesPreview.viewDetails")}
                          </span>
                          <i className="ri-arrow-right-line text-xs text-[#C0C1CB] group-hover:text-[#8A7A6A] group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                        </button>
                      ))}
                    </div>

                    <div className="flex items-center gap-8 flex-wrap">
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#1E202A] border-b border-[#1E202A]/25 pb-0.5 hover:border-[#8A7A6A] hover:text-[#8A7A6A] transition-all duration-300 cursor-pointer whitespace-nowrap"
                        style={{ letterSpacing: "0.02em" }}
                      >
                        {t("servicesPreview.consultationCta")}
                        <i className="ri-arrow-right-line text-xs" />
                      </Link>
                      {s.ctaExternalUrl && (
                        <a
                          href={s.ctaExternalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-[#8A7A6A] border-b border-[#8A7A6A]/30 pb-0.5 hover:border-[#8A7A6A] hover:text-[#1E202A] transition-all duration-300 cursor-pointer whitespace-nowrap"
                          style={{ letterSpacing: "0.02em" }}
                        >
                          {s.ctaExternalLabel}
                          <i className="ri-external-link-line text-xs" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                {si < services.length - 1 && <div className="w-full h-px bg-[#D0D1DB]" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServiceDetailPanel
        globalIndex={activeGlobalIndex}
        onClose={() => setActiveGlobalIndex(null)}
        onNavigate={(i) => setActiveGlobalIndex(i)}
      />
    </>
  );
}
